import { PolarityValue } from '@shared/models'

export type DataSourceType = 'social' | 'press'

export type RowType =
  | 'positive'
  | 'negative'
  | 'favorable'
  | 'unfavorable'
  | 'neutral'
  | 'positiveFavorable'
  | 'negativeUnfavorable'

export type ExpandedRowsState = Record<RowType, boolean>

export type RulesState = {
  social: {
    positiveGeneral: string
    positiveSpecific: string
    negativeGeneral: string
    negativeSpecific: string
    favorableGeneral: string
    favorableSpecific: string
    unfavorableGeneral: string
    unfavorableSpecific: string
    neutralRules: string
  }
  press: {
    positiveGeneral: string
    positiveSpecific: string
    negativeGeneral: string
    negativeSpecific: string
    favorableGeneral: string
    favorableSpecific: string
    unfavorableGeneral: string
    unfavorableSpecific: string
    neutralRules: string
  }
  global: {
    contextRules: string
    priorityFirstStage: string
    prioritySecondStage: string
  }
}

export interface TagOption {
  id: string
  text: string
}

export type TermsState = {
  socialPositivePublishersTags: TagOption[]
  socialNegativePublishersTags: TagOption[]
  socialFavorablePublishersTags: TagOption[]
  socialUnfavorablePublishersTags: TagOption[]
  socialPositiveProprietaryTags: TagOption[]
  socialNegativeProprietaryTags: TagOption[]
  socialFavorableProprietaryTags: TagOption[]
  socialUnfavorableProprietaryTags: TagOption[]
  pressPositiveChannelsTags: TagOption[]
  pressNegativeChannelsTags: TagOption[]
  pressFavorableChannelsTags: TagOption[]
  pressUnfavorableChannelsTags: TagOption[]
  pressPositiveProprietaryTags: TagOption[]
  pressNegativeProprietaryTags: TagOption[]
  pressFavorableProprietaryTags: TagOption[]
  pressUnfavorableProprietaryTags: TagOption[]
  socialPositiveFavorablePublishersTags: TagOption[]
  socialPositiveFavorableProprietaryTags: TagOption[]
  socialNegativeUnfavorablePublishersTags: TagOption[]
  socialNegativeUnfavorableProprietaryTags: TagOption[]
  pressPositiveFavorableChannelsTags: TagOption[]
  pressPositiveFavorableProprietaryTags: TagOption[]
  pressNegativeUnfavorableChannelsTags: TagOption[]
  pressNegativeUnfavorableProprietaryTags: TagOption[]
}

export type PolarityType = 'POSITIVO' | 'NEGATIVO' | 'FAVORAVEL' | 'DESFAVORAVEL' | 'NEUTRO'

export const CONSTANTS = {
  MAX_RULES_PER_POLARITY: 15,
  SOCIAL_MEDIA_DS_ID: 1,
  PRESS_DS_ID: 2,
}

export const DATA_SOURCE_IDS = {
  SOCIAL_MEDIA: 1,
  PRESS: 2,
} as const

export function parseRules(rulesText: string | undefined): string[] {
  if (!rulesText || rulesText.trim() === '') return []

  return rulesText
    .split(';')
    .map((rule) => rule.trim())
    .filter((rule) => rule.length > 0)
}

export function getRulesCount(rulesText: string | undefined): number {
  return parseRules(rulesText).length
}

export function getCurrentRules(rules: RulesState, activeTab: DataSourceType) {
  return activeTab === 'social' ? rules.social : rules.press
}

export function getPolarityIcon(polarityType: string): string {
  switch (polarityType) {
    case 'POSITIVO':
      return PolarityValue.POSITIVE
    case 'NEGATIVO':
      return PolarityValue.NEGATIVE
    case 'FAVORÁVEL':
      return PolarityValue.FAVORABLE
    case 'DESFAVORÁVEL':
      return PolarityValue.UNFAVORABLE
    case 'NEUTRO':
      return PolarityValue.NEUTRAL
    default:
      return PolarityValue.NEUTRAL
  }
}

export function getSpecificRuleNumber(polarityType: string, ruleIndex: number): number {
  const baseNumbers = {
    POSITIVO: 1,
    NEGATIVO: 16,
    FAVORÁVEL: 31,
    DESFAVORÁVEL: 46,
    NEUTRO: 61,
  }

  const baseNumber = baseNumbers[polarityType as keyof typeof baseNumbers] || 1
  return baseNumber + ruleIndex
}

export function hasValidSpecificRulesForPolarity(
  rules: RulesState,
  dataSource: DataSourceType,
  field: string,
): boolean {
  const currentRules = getCurrentRules(rules, dataSource)
  const rulesText = currentRules[field as keyof typeof currentRules]
  return parseRules(rulesText).length > 0
}

export function isPromptConfigurationComplete(rules: RulesState, excludedDataSources: number[]): boolean {
  const isSocialExcluded = excludedDataSources.includes(DATA_SOURCE_IDS.SOCIAL_MEDIA)
  const isPressExcluded = excludedDataSources.includes(DATA_SOURCE_IDS.PRESS)

  if (isSocialExcluded && isPressExcluded) {
    return false
  }

  const socialComplete =
    isSocialExcluded ||
    [
      hasValidSpecificRulesForPolarity(rules, 'social', 'positiveSpecific'),
      hasValidSpecificRulesForPolarity(rules, 'social', 'negativeSpecific'),
      hasValidSpecificRulesForPolarity(rules, 'social', 'favorableSpecific'),
      hasValidSpecificRulesForPolarity(rules, 'social', 'unfavorableSpecific'),
      hasValidSpecificRulesForPolarity(rules, 'social', 'neutralRules'),
    ].every(Boolean)

  const pressComplete =
    isPressExcluded ||
    [
      hasValidSpecificRulesForPolarity(rules, 'press', 'positiveSpecific'),
      hasValidSpecificRulesForPolarity(rules, 'press', 'negativeSpecific'),
      hasValidSpecificRulesForPolarity(rules, 'press', 'favorableSpecific'),
      hasValidSpecificRulesForPolarity(rules, 'press', 'unfavorableSpecific'),
      hasValidSpecificRulesForPolarity(rules, 'press', 'neutralRules'),
    ].every(Boolean)

  return socialComplete && pressComplete
}

export function getRowHeight(expandedRows: ExpandedRowsState, rowType: RowType): string {
  return expandedRows[rowType] ? 'auto' : '60px'
}

export function getTermsForPolarity(polarity: string, activeTab: DataSourceType, termsState: TermsState) {
  const isActiveTabSocial = activeTab === 'social'

  if (isActiveTabSocial) {
    const publishersTags =
      polarity === 'POSITIVO' ? termsState.socialPositivePublishersTags
      : polarity === 'NEGATIVO' ? termsState.socialNegativePublishersTags
      : polarity === 'FAVORÁVEL' ? termsState.socialFavorablePublishersTags
      : termsState.socialUnfavorablePublishersTags

    const proprietaryTags =
      polarity === 'POSITIVO' ? termsState.socialPositiveProprietaryTags
      : polarity === 'NEGATIVO' ? termsState.socialNegativeProprietaryTags
      : polarity === 'FAVORÁVEL' ? termsState.socialFavorableProprietaryTags
      : termsState.socialUnfavorableProprietaryTags

    return { publishersTags, proprietaryTags, channelsTags: [] }
  } else {
    const channelsTags =
      polarity === 'POSITIVO' ? termsState.pressPositiveChannelsTags
      : polarity === 'NEGATIVO' ? termsState.pressNegativeChannelsTags
      : polarity === 'FAVORÁVEL' ? termsState.pressFavorableChannelsTags
      : termsState.pressUnfavorableChannelsTags

    const proprietaryTags =
      polarity === 'POSITIVO' ? termsState.pressPositiveProprietaryTags
      : polarity === 'NEGATIVO' ? termsState.pressNegativeProprietaryTags
      : polarity === 'FAVORÁVEL' ? termsState.pressFavorableProprietaryTags
      : termsState.pressUnfavorableProprietaryTags

    return { channelsTags, proprietaryTags, publishersTags: [] }
  }
}

export function updateTermsState(
  polarity: PolarityType,
  activeTab: DataSourceType,
  termType: 'publishers' | 'channels' | 'proprietary',
  tags: TagOption[],
) {
  const isActiveTabSocial = activeTab === 'social'
  const polarityKey =
    polarity === 'POSITIVO' ? 'Positive'
    : polarity === 'NEGATIVO' ? 'Negative'
    : polarity === 'FAVORAVEL' ? 'Favorable'
    : 'Unfavorable'

  if (isActiveTabSocial) {
    if (termType === 'publishers') {
      return { [`social${polarityKey}PublishersTags`]: tags }
    } else {
      return { [`social${polarityKey}ProprietaryTags`]: tags }
    }
  } else {
    if (termType === 'channels') {
      return { [`press${polarityKey}ChannelsTags`]: tags }
    } else {
      return { [`press${polarityKey}ProprietaryTags`]: tags }
    }
  }
}

export function isDataSourceExcluded(dataSource: DataSourceType, excludedDataSources: number[]): boolean {
  const dataSourceId = dataSource === 'social' ? DATA_SOURCE_IDS.SOCIAL_MEDIA : DATA_SOURCE_IDS.PRESS
  return excludedDataSources.includes(dataSourceId)
}
